import { Button } from "@/components/ui/button";
import { Zap, Image as ImageIcon } from "lucide-react";
import { GenieBranding } from "@/types/ui/branding";

interface AgentBrandingPreviewProps {
  agentName: string;
  branding: GenieBranding | null;
  onApply: () => void;
  isApplying?: boolean;
}

const AgentBrandingPreview = ({ agentName, branding, onApply, isApplying }: AgentBrandingPreviewProps) => {
  const hasAnyBranding = branding && (
    branding.logoUrl ||
    branding.primaryColor ||
    branding.secondaryColor ||
    branding.embedStyle?.headerColor
  );

  if (!hasAnyBranding) {
    return (
      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
        <p className="text-sm text-gray-500">
          <span className="font-medium text-gray-700">{agentName}</span> has no custom branding configured.
        </p>
      </div>
    );
  }

  const colors = [
    branding?.primaryColor,
    branding?.secondaryColor,
    branding?.embedStyle?.headerColor,
  ].filter(Boolean) as string[];

  return (
    <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg p-4 border border-blue-100">
      <div className="flex items-start gap-4">
        {/* Logo */}
        <div className="flex-shrink-0">
          {branding?.logoUrl ? (
            <img
              src={branding.logoUrl}
              alt={`${agentName} logo`}
              className="w-12 h-12 rounded-lg object-contain bg-white border border-gray-200"
            />
          ) : (
            <div className="w-12 h-12 rounded-lg bg-white border border-gray-200 flex items-center justify-center">
              <ImageIcon className="w-5 h-5 text-gray-400" />
            </div>
          )}
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-semibold text-gray-900 truncate">{agentName}</h4>

          {/* Color swatches */}
          {colors.length > 0 && (
            <div className="flex items-center gap-1.5 mt-2">
              <span className="text-xs text-gray-500 mr-1">Colors:</span>
              {colors.map((color, index) => (
                <div
                  key={index}
                  className="w-5 h-5 rounded border border-gray-300 shadow-sm"
                  style={{ backgroundColor: color }}
                  title={color}
                />
              ))}
            </div>
          )}
        </div>

        {/* Apply button */}
        <Button
          onClick={onApply}
          size="sm"
          disabled={isApplying}
          className="flex-shrink-0 gap-1.5 bg-gradient-to-r from-primary to-pink-500 hover:from-primary/90 hover:to-pink-500/90"
        >
          <Zap className="h-3.5 w-3.5" />
          {isApplying ? "Applying..." : "Apply"}
        </Button>
      </div>
    </div>
  );
};

export default AgentBrandingPreview;
